import React, { useEffect, useMemo, useState } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import {
  Select,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
} from '@material-ui/core';
import { get, isEmpty, isNil } from 'lodash';
import { translate } from 'containers/services/translate';
import messages from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/messages';
import {
  isOtherReasonSelected,
  getInjuredVictimReasons,
  hasOtherReason,
} from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/utils';
import { JudgementADDDocumentType } from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import { Listings } from 'containers/AppContainer/typings';
import { Config } from 'containers/AppContainer/typings';
import { CIVIL_JUDGEMENT_VICTIM_FIELDS } from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/constants';
import { EXPERTISES } from 'containers/data-structure';

export interface CivilWithNonCompensationProps {
  intl: IntlShape;
  setJudgementOnMeritsRequest: Function;
  judgementOnMeritsRequest: any;
  judgementAddDocuments: JudgementADDDocumentType[];
  listings: Listings;
  mode?: string;
  victim?: any;
  vehicle?: any;
  expertiseJudgementAddDocumentId?: number;
  allJudgementDocuments?: any;
  withAccountingExpertise?: any;
  sharedResponsability?: any;
  expertiseType?: string;
  isDisabled: boolean;
  judgementAddId?: number;
}

const CivilWithNonCompensation: React.FC<CivilWithNonCompensationProps> = (
  props,
): JSX.Element => {
  const [
    civilWithNonCompensationItems,
    setCivilWithNonCompensationItems,
  ] = useState<any>({});

  const id = useMemo(
    () =>
      props.expertiseJudgementAddDocumentId
        ? `${!!props.victim ? props.victim?.id : props.vehicle?.id}-${
            props.expertiseJudgementAddDocumentId
          }`
        : !!props.victim
        ? props.victim?.id
        : props.vehicle?.id,
    [props.expertiseJudgementAddDocumentId],
  );

  const item = !!props.victim ? props.victim : props.vehicle;
  useEffect(() => {
    setCivilWithNonCompensationItems({
      ...get(props, 'judgementOnMeritsRequest.civjvm', []),
      ...get(props, 'judgementOnMeritsRequest.civjVehicle', []),
    });
  }, [get(props, 'judgementOnMeritsRequest', {})]);
  /* istanbul ignore next */
  const handleChange = (item: any) => (
    event: React.ChangeEvent<{ name?: string | undefined; value: any }>,
  ) => {
    const values = { ...civilWithNonCompensationItems };
    const id = props.expertiseJudgementAddDocumentId
      ? `${item.id}-${props.expertiseJudgementAddDocumentId}`
      : item.id;

    const civjvmProps = get(props, 'judgementOnMeritsRequest.civjvm', []);
    const civjVehicleProps = get(
      props,
      'judgementOnMeritsRequest.civjVehicle',
      [],
    );
    let accountingJaddId: any = null;
    if (event.target.name) {
      values[id] = {
        ...(!!props.victim
          ? { ...civjvmProps[id], victimKey: item.victimKey, victimId: item.id }
          : { ...civjVehicleProps[id], vehicleId: item.id }),
        expertiseJudgementAddDocumentId: props.expertiseJudgementAddDocumentId,
        [event.target.name]: event.target.value,
        otherReason: !hasOtherReason(
          get(props.listings, 'civilJudgementVictimReason', []),
          get(values[id], CIVIL_JUDGEMENT_VICTIM_FIELDS.REASON, ''),
        )
          ? null
          : event.target.name === CIVIL_JUDGEMENT_VICTIM_FIELDS.OTHER_REASON
          ? event.target.value
          : null,
      };
    }
    if (
      get(props.withAccountingExpertise, 'hasAccountingExpertise') &&
      props.expertiseType !== EXPERTISES.ACCOUNTING_EXPERTISE
    ) {
      const jadd = props.allJudgementDocuments.find((item: any) =>
        get(item, 'expertises', []).some(
          (expertise: any) =>
            expertise.expertiseJudgementAddDocumentId ===
            get(
              props.withAccountingExpertise,
              'accountingExpertise.expertiseJudgementAddDocumentId',
            ),
        ),
      );
      accountingJaddId = get(jadd, 'id');
    }
    const civj =
      get(props.judgementOnMeritsRequest, 'civjs', []).find(
        (item: any) => item.judgementAddDocumentId === accountingJaddId,
      ) || {};
    setCivilWithNonCompensationItems({
      ...civjvmProps,
      ...civjVehicleProps,
      [id]: values[id],
    });

    props.setJudgementOnMeritsRequest(
      Object.assign(
        {},
        { ...props.judgementOnMeritsRequest },
        {
          ...(props.victim && {
            civjvm: {
              ...civjvmProps,
              ...(!!props.victim && { [id]: values[id] }),
            },
          }),
          ...(props.vehicle && {
            civjVehicle: {
              ...civjVehicleProps,
              ...(!!props.vehicle && { [id]: values[id] }),
            },
          }),
          ...(isEmpty(civj) &&
            !isNil(accountingJaddId) &&
            props.judgementAddId !== accountingJaddId && {
              civjs: [
                ...get(props.judgementOnMeritsRequest, 'civjs', []),
                {
                  judgementAddDocumentId: accountingJaddId,
                  responsibilityShare: props.sharedResponsability,
                  civjmede: [],
                  civjacce: [],
                  civjmece: [],
                },
              ],
            }),
        },
      ),
    );
  };

  return (
    <Grid container item spacing={2}>
      <Grid item xs={4}>
        <FormControl fullWidth>
          <InputLabel>
            {translate({ ...messages.reasonField }, props)}
          </InputLabel>
          <Select
            name={CIVIL_JUDGEMENT_VICTIM_FIELDS.REASON}
            value={get(civilWithNonCompensationItems[id], 'reasonId', '')}
            onChange={handleChange(item)}
            disabled={props.isDisabled}
          >
            {getInjuredVictimReasons(props.listings, props.mode).map(
              (item: Config) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.title}
                </MenuItem>
              ),
            )}
          </Select>
        </FormControl>
      </Grid>
      {isOtherReasonSelected(
        get(props.listings, 'civilJudgementVictimReason', []),
        get(civilWithNonCompensationItems[id], 'reasonId', ''),
        props.mode,
      ) && (
        <Grid item xs={4} className="other-item">
          <TextField
            onChange={handleChange(item)}
            name={CIVIL_JUDGEMENT_VICTIM_FIELDS.OTHER_REASON}
            value={get(civilWithNonCompensationItems[id], 'otherReason', '')}
            disabled={props.isDisabled}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default injectIntl(CivilWithNonCompensation);
